import React from 'react';
import { observer } from 'mobx-react';
import Button from '../../../../components/Button';
import styles from './JobProgressionMessageContent.m.sass';
import { formatDateSimple } from '../../../../utils/dateUtil';
import LegendBuilderYesNoIcon from '../../../../assets/icons/legend-builder-yes-no.i.svg';
import IconSvg from '../../../../components/IconSvg';

/**
 * Prompt Message Content
 */
export default observer(function JobProgressionMessageContent(_ref) {
  var jobProgressionStep = _ref.jobProgressionStep,
    onViewRejectDraft = _ref.onViewRejectDraft,
    onViewApproveDraft = _ref.onViewApproveDraft,
    _ref$isReadOnly = _ref.isReadOnly,
    isReadOnly = _ref$isReadOnly === void 0 ? false : _ref$isReadOnly;
  switch (jobProgressionStep.status) {
    case 'DRAFT_IN_REVIEW':
      if (isReadOnly) {
        return /*#__PURE__*/React.createElement(DraftInReviewReadOnlyMessageContent, {
          jobProgressionStep: jobProgressionStep
        });
      } else {
        return /*#__PURE__*/React.createElement(DraftInReviewMessageContent, {
          jobProgressionStep: jobProgressionStep,
          onViewRejectDraft: onViewRejectDraft,
          onViewApproveDraft: onViewApproveDraft
        });
      }
    case 'DRAFT_APPROVED':
      return /*#__PURE__*/React.createElement(DraftApprovedMessageContent, {
        jobProgressionStep: jobProgressionStep
      });
    case 'DRAFT_REJECTED':
      return /*#__PURE__*/React.createElement(DraftRejectedMessageContent, {
        jobProgressionStep: jobProgressionStep
      });
    default:
      return /*#__PURE__*/React.createElement(React.Fragment, null);
  }
});
var DraftInReviewReadOnlyMessageContent = observer(function DraftInReviewReadOnlyMessageContent(_ref2) {
  var jobProgressionStep = _ref2.jobProgressionStep;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.root
  }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(CardContent, {
    title: "The return is now ready for review",
    visual: /*#__PURE__*/React.createElement(IconSvg, {
      size: 36,
      color: "#007be7"
    }, /*#__PURE__*/React.createElement(LegendBuilderYesNoIcon, null))
  }, formatDateSimple(jobProgressionStep.dateCreated, {
    longDate: true,
    time: true
  }))));
});
var DraftInReviewMessageContent = observer(function DraftInReviewMessageContent(_ref3) {
  var jobProgressionStep = _ref3.jobProgressionStep,
    onViewRejectDraft = _ref3.onViewRejectDraft,
    onViewApproveDraft = _ref3.onViewApproveDraft;
  var viewRejectDraft = function viewRejectDraft(e) {
    // Prevent timestamp visibility toggle
    e.stopPropagation();
    onViewRejectDraft(jobProgressionStep);
  };
  var viewApproveDraft = function viewApproveDraft(e) {
    // Prevent timestamp visibility toggle
    e.stopPropagation();
    onViewApproveDraft(jobProgressionStep);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: styles.root
  }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(CardContent, {
    title: "The return is now ready for review",
    visual: /*#__PURE__*/React.createElement(IconSvg, {
      size: 36,
      color: "#007be7"
    }, /*#__PURE__*/React.createElement(LegendBuilderYesNoIcon, null))
  }, "Do you approve the latest draft return?"), !jobProgressionStep.response && /*#__PURE__*/React.createElement(CardFooter, null, /*#__PURE__*/React.createElement(CardFooterSpacer, null), /*#__PURE__*/React.createElement(CardFooterItem, null, /*#__PURE__*/React.createElement(Button, {
    type: "secondary",
    onClick: viewRejectDraft,
    dataTest: "job-progression-set-draft-rejected-button"
  }, "Reject")), /*#__PURE__*/React.createElement(CardFooterItem, null, /*#__PURE__*/React.createElement(Button, {
    type: "primary",
    onClick: viewApproveDraft,
    dataTest: "job-progression-set-draft-approved-button"
  }, "Approve")))));
});
var DraftApprovedMessageContent = observer(function DraftApprovedMessageContent(_ref4) {
  var jobProgressionStep = _ref4.jobProgressionStep;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.root
  }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(CardContent, {
    title: "The return has been approved",
    visual: /*#__PURE__*/React.createElement(IconSvg, {
      size: 36,
      color: "#007be7"
    }, /*#__PURE__*/React.createElement(LegendBuilderYesNoIcon, null))
  }, formatDateSimple(jobProgressionStep.dateCreated, {
    longDate: true,
    time: true
  }))));
});
var DraftRejectedMessageContent = observer(function DraftRejectedMessageContent(_ref5) {
  var jobProgressionStep = _ref5.jobProgressionStep;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.root
  }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(CardContent, {
    title: "Changes have been requested",
    visual: /*#__PURE__*/React.createElement(IconSvg, {
      size: 36,
      color: "#007be7"
    }, /*#__PURE__*/React.createElement(LegendBuilderYesNoIcon, null))
  }, formatDateSimple(jobProgressionStep.dateCreated, {
    longDate: true,
    time: true
  }))));
});

/**
 * Card
 */
export function Card(_ref6) {
  var children = _ref6.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.card
  }, children);
}

/**
 * Card Content
 */
export function CardContent(_ref7) {
  var visual = _ref7.visual,
    title = _ref7.title,
    children = _ref7.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.cardContent
  }, visual && /*#__PURE__*/React.createElement("div", {
    className: styles.cardContentVisual
  }, visual), /*#__PURE__*/React.createElement("div", {
    className: styles.cardContentCopy
  }, title && /*#__PURE__*/React.createElement("h3", {
    className: styles.cardContentTitle
  }, title), /*#__PURE__*/React.createElement("p", {
    className: styles.cardContentBody
  }, children)));
}

/**
 * Card Footer
 */
export function CardFooter(_ref8) {
  var children = _ref8.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.cardFooter
  }, children);
}

/**
 * Card Footer Item
 */
export function CardFooterItem(_ref9) {
  var children = _ref9.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.cardFooterItem
  }, children);
}

/**
 * Card Footer Spacer
 */
export function CardFooterSpacer(_ref10) {
  var children = _ref10.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.cardFooterSpacer
  }, children);
}

/**
 * Card Footer Copy
 */
export function CardFooterCopy(_ref11) {
  var title = _ref11.title,
    children = _ref11.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.cardFooterCopy
  }, title && /*#__PURE__*/React.createElement("h6", {
    className: styles.cardFooterCopyTitle
  }, title), /*#__PURE__*/React.createElement("div", {
    className: styles.cardFooterCopyBody
  }, children));
}
//# sourceMappingURL=index.js.map
